/**
 * Quests as a top-bar control on phones.
 *
 * The desktop rail is a fine use of a wide screen and a bad use of a phone page
 * box: three command cards plus a permanent quest strip is information overload,
 * and the strip spent a whole content row to say "New quests arrive daily."
 * So on a phone the strip leaves the lobby flow and its content becomes a
 * button in the account bar that expands it.
 *
 * It presents the SAME `#lobby-quests` element the desktop rail uses. That is
 * deliberate: `quests.js` keeps exactly one render path, so the count on the
 * button and the list inside the sheet can never disagree, and a future quest
 * type needs no second implementation. This module owns visibility and nothing
 * else — it never reads, renders or refreshes quest data.
 */

let LobbyQuestsToggle = {};

LobbyQuestsToggle = (function () {
    let button = null;
    let panel = null;
    let bound = false;

    function resolve() {
        button = document.getElementById('lobby-quests-btn');
        panel = document.getElementById('lobby-quests');
        return Boolean(button && panel);
    }

    function isOpen() {
        return Boolean(panel && panel.classList.contains('is-open'));
    }

    function setOpen(open) {
        if (!panel || !button) return;
        panel.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
    }

    function close({ restoreFocus = false } = {}) {
        if (!isOpen()) return;
        setOpen(false);
        if (restoreFocus) {
            try { button.focus({ preventScroll: true }); } catch (_) { button.focus?.(); }
        }
    }

    /**
     * Mirror the panel's own count onto the control. Read from the rendered
     * element rather than from quest data so there is one source of truth: if
     * the sheet says 2/3, the button cannot say anything else. A blank panel
     * count (no quest read yet) is a blank pill count, and a count already
     * shown is not written again.
     */
    function syncCount() {
        if (!resolve()) return;
        const source = document.getElementById('lobby-quests-count');
        const target = button.querySelector('.lobby-quests-btn-count');
        const count = source?.textContent || '';
        if (source && target && target.textContent !== count) target.textContent = count;
    }

    function onDocumentPointerDown(event) {
        if (!isOpen()) return;
        const target = event.target;
        // A press inside the sheet or on the control itself is not a dismissal;
        // the control's own handler owns the toggle.
        if (panel.contains(target) || button.contains(target)) return;
        close();
    }

    function onKeydown(event) {
        if (event.key !== 'Escape' || !isOpen()) return;
        event.preventDefault();
        close({ restoreFocus: true });
    }

    function init() {
        if (!resolve()) return;
        syncCount();
        if (bound) return;
        bound = true;
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            setOpen(!isOpen());
            if (isOpen()) syncCount();
        });
        document.addEventListener('pointerdown', onDocumentPointerDown, true);
        document.addEventListener('keydown', onKeydown, true);
    }

    return { init, close, syncCount, isOpen };
})();

export { LobbyQuestsToggle };
